import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  Inject,
  ChangeDetectorRef,
} from '@angular/core';

import { InfiniteScrollCustomEvent, IonContent } from '@ionic/angular';
import {
  Subscription,
  BehaviorSubject,
  combineLatest,
  merge,
  forkJoin,
  Observable,
  filter,
  of,
  catchError,
  switchMap,
  throttleTime,
  tap,
  take,
  finalize,
} from 'rxjs';
import moment from 'moment';
import cloneDeep from 'lodash/cloneDeep';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';

import { WebSocketEventsEnum } from '@shared/websocket';
import { Resource } from '@shared/resource/models';
import { Service } from '@shared/service/models';
import { AuthUser } from '@shared/signup/models';
import { Location } from '@shared/location/models';
import { UserLocationRole } from '@shared/user/enums';
import { FieldType } from '@shared/restaurant/enums';
import { CheckoutStatus } from '@shared/stripe/enums';
import { Party } from '@shared/party/models';
import {
  IParty,
  IPartyNotification,
  DateTimeService,
  IPageData,
  PrintService,
  PageTitleService,
  SearchBarComponent,
} from '@tablesready/common-ui';
import { AppIcon, AppIconColor, AppIconSize } from '@tablesready/frontend-icons';
import { HeaderTitleService } from '@tablesready/frontend-header';
import { AuthRestaurantService, SocketService } from '@tablesready/frontend-store';
import { AlertsService, ClockService, IAlertTypes } from '@tablesready/frontend-alerts-bar';
import { APP_VARIABLES_TOKEN, AppVariables } from '@tablesready/frontend-variables';
import { FrontendBreakpointsService } from '@tablesready/frontend-breakpoints';
import { AuthService } from '@tablesready/frontend-auth';
import {
  MarkPartyService,
  PartiesService,
  ResourcesService,
  ServicesService,
  ReservationService,
  PricePlansRestrictionsService,
  PricePlansRestrictionsModalService,
} from '../services';


@Component({
  selector: 'tablesready-reservations',
  templateUrl: './reservations.component.html',
  styleUrls: ['./reservations.component.scss'],
})
export class ReservationsComponent implements OnInit, OnDestroy {
  @ViewChild(IonContent) ionContent?: IonContent;
  @ViewChild('searchBarRef') searchBarRef?: SearchBarComponent;
  reservations: IParty[] = [];
  resCount = 0;
  services: Service[] = [];
  resources: Resource[] = [];
  reservationDateFilter: NgbDateStruct | string = '';
  navItems = [{
    href: '/import',
    name: 'Import',
    icon: AppIcon.import,
  }];

  now;
  displayCompact;
  user: AuthUser;
  location: Location;
  timezone: string;
  debounceMs = 200;
  minDate: NgbDateStruct;
  maxDate: NgbDateStruct;
  userLocationRole: UserLocationRole;
  isPrinting = false;
  isSubmitting = false;

  icon = AppIcon;
  size = AppIconSize;
  color = AppIconColor;
  sendIconActive: Record<number, boolean> = {};
  fieldTypes = FieldType;

  // Infinite scroll
  page = 1;
  limit = 40;
  disableScroll: boolean;
  loading: boolean;
  sortBy = 'quoted_time';
  sortReverse = false;
  reservations_search_query = '';
  currentParty: IParty;
  selectedTimeForReservation: number;
  selectedEndTimeForReservation: number;
  selectedResourceForReservation: Resource;
  isMobile = false;

  private availableForStarterPlan = false;
  private availableForBusinessPlan = false;
  private ionSubscription = new Subscription();
  private infiniteScrollEvent?: InfiniteScrollCustomEvent;
  private readonly subscriptions = new Subscription();
  private readonly reservationsData = new BehaviorSubject<IPageData>({
    page: this.page,
    sortBy: this.sortBy,
    sortReverse: this.sortReverse,
    limit: this.limit,
    search: this.reservations_search_query,
    date: '',
  });

  @ViewChild('tableOfReservations') private readonly tableOfReservations: ElementRef;

  constructor(
    readonly service: ReservationService,
    private readonly cdr: ChangeDetectorRef,
    private readonly authService: AuthService,
    private readonly clockService: ClockService,
    private readonly servicesService: ServicesService,
    private readonly resourcesService: ResourcesService,
    private readonly alertsService: AlertsService,
    private readonly socketsService: SocketService,
    private readonly partiesService: PartiesService,
    private readonly markPartyService: MarkPartyService,
    private readonly dateTimeService: DateTimeService,
    private readonly printService: PrintService,
    private readonly pageTitleService: PageTitleService,
    private readonly authRestaurantService: AuthRestaurantService,
    private readonly headerService: HeaderTitleService,
    private readonly breakpointObserver: FrontendBreakpointsService,
    private readonly pricePlansRestrictionsService: PricePlansRestrictionsService,
    private readonly pricePlansRestrictionsModalService: PricePlansRestrictionsModalService,
    @Inject(APP_VARIABLES_TOKEN) private readonly appVariables: AppVariables,
  ) { }

  onIonInfinite(ev: InfiniteScrollCustomEvent): void {
    this.infiniteScrollEvent = ev;
    this.loadMore();
  }

  ionViewWillEnter(): void {
    void this.ionContent?.scrollToTop(0);
    this.ionSubscription = new Subscription();

    const authUserSub = this.authRestaurantService.selectWithAccount()
      .pipe(
        switchMap(user => combineLatest([
          of(user),
          this.resourcesService.selectAllEnabled().pipe(tap(resources => this.resources = resources)),
          this.servicesService.selectAllEnabled().pipe(tap(services => this.services = services)),
        ])),
        tap(([user]) => {
          const location = user.location;
          this.user = user;
          this.location = location;
          this.timezone = location.timezone;
          this.userLocationRole = user.locationUser.role;
          this.updateUserSettings(location);
          this.availableForStarterPlan = this.pricePlansRestrictionsService.availableForStarterPlan(this.user.account.subscriptionType, this.location.recurly);
          this.availableForBusinessPlan = this.pricePlansRestrictionsService.availableForBusinessPlan(this.user.account.subscriptionType, this.location.recurly);
          this.minDate = this.dateTimeService.momentToNgbDate(moment.tz(this.timezone).subtract(14, 'days'));
          this.maxDate = {
            ...this.minDate,
            year: this.minDate.year + 1,
            day: this.minDate.day + 14,
          };
          this.emitUpdateReservationsEvent();
        }),
      ).subscribe();
    this.ionSubscription.add(authUserSub);

    const dataSub = this.reservationsData.asObservable()
      .pipe(
        // Check debounceMs
        throttleTime(this.debounceMs),
        switchMap(options => forkJoin([this.updateReservations(options), of(options)])),
        tap(([data, options]) => {
          const { parties, count } = data;

          if (this.infiniteScrollEvent) {
            void this.infiniteScrollEvent.target.complete();
            this.infiniteScrollEvent = null;
          }

          if (!parties.length && options.page !== 1) {
            this.disableScroll = true;
            this.loading = false;

            return false;
          }
          this.updateReservation(cloneDeep(parties), options.page);
          this.loading = false;
          this.updatePartiesCount(count);
          this.page = options.page + options.limit / this.limit;
          if (this.isPrinting) {
            // SetTimeout for waiting while DOM is updated
            setTimeout(() => {
              this.printService.print(this.tableOfReservations);
            }, 0);
            this.isPrinting = false;
          }
        }),
      )
      .subscribe();
    this.ionSubscription.add(dataSub);
  }

  ionViewWillLeave(): void {
    if (!this.searchBarRef) {
      return;
    }

    this.searchBarRef.clearSearchField();
    this.ionSubscription.unsubscribe();
    this.deleteDate();
  }

  updateSearchQuery(query: string): void {
    this.reservations_search_query = query;
    this.page = 1;
    this.emitUpdateReservationsEvent();
  }

  trackById = (_: number, item: IParty): string => item._id;

  get getUndoMessage() {
    if (this.currentParty) {
      const party = this.currentParty;

      return this.alertsService.getTextMessage(party.name, party.size, this.location.displayOptions?.labels?.reservation, this.location.displayOptions?.labels?.customer);
    }

    return '';
  }

  get showCustomFieldsData(): boolean {
    return this.availableForBusinessPlan
      && this.location.partyFields
      && this.location.partyFields.length > 0;
  }

  chatTooltip(phoneNumber?: string): string {
    if (this.isMobile) {
      return '';
    }

    return !phoneNumber ? 'No phone provided' : 'Chat';
  }

  ngOnInit() {
    this.pageTitleService.setTitle('Reservations');
    this.headerService.setDataForRoute('reservations', { title: 'Reservations' });

    this.subscriptions.add(this.clockService.clock$.subscribe(now => this.now = now));

    const breakpointObserverSub = this.breakpointObserver.observeXsBreakpoint()
      .pipe(tap(isMobile => this.isMobile = isMobile)).subscribe();
    this.subscriptions.add(breakpointObserverSub);

    // Sockets
    const socketSub = merge(
      this.socketsService.getEventStream(WebSocketEventsEnum.parties),
      this.socketsService.getEventStream(WebSocketEventsEnum.reservations),
      this.socketsService.getEventStream(WebSocketEventsEnum.TextSmsEvent),
      this.socketsService.getEventStream(WebSocketEventsEnum.VoiceSmsEvent),
      this.socketsService.getEventStream(WebSocketEventsEnum.InboundSmsEvent),
    )
      .pipe(
        throttleTime(this.debounceMs),
        tap(() => {
          const statusPage = Math.ceil(this.reservations.length / this.limit) || 1;
          this.reservationsEventHandler(statusPage);
        }),
      )
      .subscribe();
    this.subscriptions.add(socketSub);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  loadMore() {
    if (this.loading) {
      return false;
    }
    this.loading = true;
    this.emitUpdateReservationsEvent(this.page);
  }

  updateUserSettings(location: Location) {
    this.displayCompact = location.displayOptions.compact || false;
    if (location.noteOptions && !Array.isArray(location.noteOptions) && location.noteOptions !== '') {
      location.noteOptions = (location.noteOptions as string).split(',');
    }
  }

  updateReservations(options): Observable<any> {
    this.loading = true;
    const { page, limit, sortBy, sortReverse, search, type, date } = options;
    const sort = {};
    sort[sortBy] = sortReverse ? -1 : 1;

    return this.partiesService.getReservations({
      page,
      limit,
      search,
      sort,
      type,
      date,
    });
  }

  updateReservation(reservations, page = 1): void {
    if (page === 1) {
      this.reservations = reservations;
    } else {
      this.reservations = this.partiesService.unique([...this.reservations, ...reservations]);
    }
    // This.updatePartiesFilter();
  }

  reservationsEventHandler(statusPage = 1): void {
    this.page = 1;
    if (this.loading) {
      return;
    }

    this.disableScroll = false;
    this.emitUpdateReservationsEvent(1, statusPage);
  }

  emitUpdateReservationsEvent(page = 1, statusPage = 1): void {
    const isAuthenticated = this.authService.isAuthenticated();

    if (isAuthenticated) {
      const date = this.reservationDateFilter === '' ? '' : this.dateTimeService
        .ngbDateToMoment(this.reservationDateFilter as NgbDateStruct, this.timezone)
        .valueOf();

      const initOptions = {
        page,
        sortBy: this.sortBy,
        sortReverse: this.sortReverse,
        limit: this.limit * statusPage,
        search: this.reservations_search_query,
        date,
      };

      this.reservationsData.next(initOptions);
    }
  }

  getAllReservations() {
    const date = this.reservationDateFilter === '' ? '' : this.dateTimeService
      .ngbDateToMoment(this.reservationDateFilter as NgbDateStruct, this.timezone)
      .valueOf();

    const initOptions = {
      sortBy: this.sortBy,
      sortReverse: this.sortReverse,
      search: this.reservations_search_query,
      date,
    };

    this.isPrinting = true;
    this.reservationsData.next(initOptions);
  }

  updatePartiesCount(partiesCount: number): void {
    this.resCount = partiesCount;
    this.headerService.setDataForRoute('reservations', { badgeCount: partiesCount });
  }

  mergeInboundOutbound(party: IParty): IPartyNotification[] {
    const messages = [];
    if (party.notifications) {
      Array.prototype.push.apply(messages, party.notifications);
    }
    if (party.inbounds) {
      Array.prototype.push.apply(messages, party.inbounds);
    }
    messages.sort((a, b) => a.time - b.time);

    return messages.slice(-3); // eslint-disable-line no-magic-numbers
  }

  confirmDelete(party: IParty): void {
    this.authRestaurantService.get();

    this.service.confirmDelete(party)
      .pipe(
        take(1),
        filter(({ data }) => data !== undefined),
        tap(({ data }) => {
          this.currentParty = party;
          party.isHidden = true;
          this.markPartyAlert(party, 'Success:', data.performRefund && party.stripeData?.totalAmount > 0);
          this.reservationsEventHandler();
        }),
      ).subscribe();
  }

  markPartyAlert(party: IParty, heading: string, refunded = false): void {
    let actions = [
      {
        label: 'Undo this',
        action: () => {
          this.markUndo();
        },
      },
    ];

    if (refunded) {
      actions = [];
    }

    party.isHidden = true;
    this.alertsService.add({
      type: IAlertTypes.success,
      local: true,
      heading,
      body: this.getUndoMessage,
      actions,
    });
    this.updatePartiesCount(this.resCount - 1);
  }

  // Add new party from day grid, with selected time and resouce
  createNewPartyInSelectedTimeResource({ resourceId, time, endTime }) {
    this.selectedResourceForReservation = this.resources.find(resource => resource._id === resourceId);
    this.selectedTimeForReservation = time;
    this.selectedEndTimeForReservation = endTime;
    if (!this.selectedResourceForReservation || !this.selectedTimeForReservation) {
      return false;
    }

    this.addEditReservation();
  }

  completeParty(party: IParty): void {
    this.service.completeParty(party, this.location)
      .pipe(take(1))
      .subscribe(() => {
        this.isSubmitting = false;
      });
  }

  addEditReservation(party?: IParty): void {
    this.service.addEditReservationReactive(
      this.user,
      this.selectedResourceForReservation,
      this.selectedTimeForReservation,
      this.selectedEndTimeForReservation,
      this.reservationDateFilter,
      false,
      undefined,
      party,
    )
      .pipe(
        take(1),
        tap((updatedParty) => {
          this.selectedTimeForReservation = undefined;
          this.disableScroll = false;
          party = {
            ...party,
            ...updatedParty,
          };
        }),
        catchError((err) => {
          this.selectedTimeForReservation = undefined;

          return of(err);
        }),
      )
      .subscribe();

    this.selectedEndTimeForReservation = null;
  }

  changeDate(): void {
    if (!this.reservationDateFilter) {
      this.reservationDateFilter = '';
    }

    this.emitUpdateReservationsEvent();
  }

  deleteDate() {
    this.reservationDateFilter = '';
    this.emitUpdateReservationsEvent();
  }

  chatParty(party: IParty): void {
    if (!this.availableForStarterPlan) {
      this.pricePlansRestrictionsModalService.display(this.user, true).subscribe();

      return;
    }

    this.service.chatParty(party, this.user);
  }

  markUndo(): void {
    const partyId: string = this.currentParty._id.toString().trim();
    this.alertsService.clear();
    this.markPartyService.setParty({
      party_id: partyId,
      seated_time: null,
      canceled_time: null,
    }).subscribe();
  }

  isArray(val: any): boolean {
    return val instanceof Array;
  }

  checkIn(party: IParty): void {
    this.service.checkIn(party, this.location)
      .pipe(
        take(1),
        finalize(() => {
          this.isSubmitting = false;
        }),
      )
      .subscribe(() => {
        this.cdr.detectChanges();
      });
  }

  showCheckInButton(party: IParty): boolean {
    return this.partiesService.showCheckInButton(party, this.now);
  }

  unpaid(party: Party): boolean {
    return party.stripeData?.status == CheckoutStatus.inProgress
      && (party.created_time + this.appVariables.UNPAID_PARTY_LIFETIME * 1000) > Date.now();
  }

  paid(party: Party): boolean {
    return party.stripeData?.status == CheckoutStatus.paid;
  }

  getRemainingSecondsToPay(partyCreatedTime: number): number {
    return partyCreatedTime + (this.appVariables.UNPAID_PARTY_LIFETIME * 1000);
  }

  displayCustomFieldCell(party: IParty, customFieldId: string): boolean {
    if (this.isMobile) {
      return party.customFields[customFieldId] !== null && party.customFields[customFieldId] !== undefined;
    }

    return true;
  }
}
